"use client";

import { type ReactNode, useEffect } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import {
  useServerProvisioningGate,
} from "@/hooks/useServerProvisioningGate";
import {
  ServerProvisioningContext,
  type ServerProvisioningContextValue,
} from "@/lib/billing/serverProvisioningContext";
import { TrialViewOnlyBanner } from "@/components/billing/TrialViewOnlyBanner";

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ServerProvisioningGate
 *
 * Route-level access control for server provisioning and management features.
 * Wraps the `/dashboard/servers` route (and any future server management routes)
 * with subscription-aware gating that supports three modes:
 *
 * **Mode: "full"** (active paid subscription)
 * - Renders children with full access context.
 * - No banner, no disabled buttons.
 *
 * **Mode: "trial_view_only"** (self-service free trial, not expired)
 * - Renders children with a `TrialViewOnlyBanner` above content.
 * - Context `isViewOnly = true` — child components disable action buttons.
 * - Limited to 1 server slot.
 *
 * **Mode: "expired"** (free trial has expired)
 * - Redirects to `/pricing?reason=trial_expired&next=<currentPath>`.
 * - Shows spinner while redirect fires.
 *
 * **Mode: "no_access"** (no subscription, no trial)
 * - Redirects to `/pricing?reason=no_subscription&next=<currentPath>`.
 * - Shows spinner while redirect fires.
 *
 * ## Context injection
 * Children read access state via `useServerProvisioningContext()` without
 * making additional Convex queries:
 * ```tsx
 * const { isViewOnly, trialDaysRemaining } = useServerProvisioningContext();
 * ```
 *
 * ## Usage in App.tsx
 * ```tsx
 * <Route
 *   path="/dashboard/servers"
 *   element={
 *     <ServerProvisioningGate>
 *       <DashboardLayout><DashboardServersPage /></DashboardLayout>
 *     </ServerProvisioningGate>
 *   }
 * />
 * ```
 */
export function ServerProvisioningGate({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const nextPath = buildNextPath(pathname, searchParams);

  const gate = useServerProvisioningGate(nextPath);

  // Redirect on expired trial or no access
  useEffect(() => {
    if (gate.isLoading) return;
    if (gate.mode === "expired" || gate.mode === "no_access") {
      router.replace(gate.redirectPath);
    }
  }, [gate.isLoading, gate.mode, gate.redirectPath, router]);

  // Loading spinner — Clerk or Convex resolving
  if (gate.isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect pending — show spinner while effect fires
  if (gate.mode === "expired" || gate.mode === "no_access") {
    return <LoadingSpinner />;
  }

  // "full" or "trial_view_only" — provide context + optional trial banner
  const contextValue: ServerProvisioningContextValue = {
    mode: gate.mode,
    isViewOnly: gate.mode === "trial_view_only",
    serverSlotLimit: gate.serverSlotLimit,
    trialDaysRemaining: gate.trialDaysRemaining,
    trialEndsAt: gate.trialEndsAt,
    isFreeTrial: gate.isFreeTrial,
    tier: gate.tier,
    status: gate.status,
  };

  return (
    <ServerProvisioningContext.Provider value={contextValue}>
      {gate.mode === "trial_view_only" && (
        <TrialViewOnlyBanner
          trialDaysRemaining={gate.trialDaysRemaining}
          trialEndsAt={gate.trialEndsAt}
          nextPath={nextPath}
        />
      )}
      {children}
    </ServerProvisioningContext.Provider>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function buildNextPath(pathname: string, searchParams: URLSearchParams): string {
  const search = searchParams.toString();
  return `${pathname}${search ? `?${search}` : ""}`;
}
